import React from 'react';
import { View, Text, TouchableOpacity, StyleSheet, ImageBackground } from 'react-native';
import { LinearGradient } from 'expo-linear-gradient';
import { Svg, Path } from 'react-native-svg';
import { Meeting } from '@/types/meetings';
import { useLanguage } from '@/contexts/LanguageContext';
import { getMeetingDisplayTime } from '@/utils/meetingUtils';
import Avatar from '@/components/Avatar';

interface FeaturedMeetingCardProps {
  meeting: Meeting;
  onPress: () => void;
}

export default function FeaturedMeetingCard({
  meeting,
  onPress,
}: FeaturedMeetingCardProps) {
  const { t } = useLanguage();
  const { displayText } = getMeetingDisplayTime(meeting.meeting_time, meeting.meeting_weekday, t);

  return (
    <TouchableOpacity style={styles.card} onPress={onPress} activeOpacity={0.8}>
      {/* ── Header ── */}
      <View style={styles.header}>
        <ImageBackground
          source={require('../assets/images/image_1763325486152.png')}
          style={styles.image}
          imageStyle={styles.imageStyle}
        >
          <LinearGradient
            colors={['rgba(255,255,255,0)', '#FFFFFF']}
            locations={[0.25, 0.7]}
            style={styles.gradient}
          >
            <View style={styles.content}>
              <Text style={styles.title} numberOfLines={2}>{meeting.meeting_name}</Text>
              <View style={styles.statusRow}>
                <Svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <Path d="M10.1016 2.46143V4.44497" stroke="#151515" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  <Path d="M5.28516 2.46143V4.44497" stroke="#151515" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  <Path fillRule="evenodd" clipRule="evenodd" d="M10.4566 3.37598L4.94039 3.37652C3.32005 3.37652 2.30859 4.27884 2.30859 5.93735V10.9302C2.30859 12.6149 3.32005 13.5381 4.94039 13.5381L10.4519 13.5376C12.077 13.5376 13.0837 12.6305 13.0837 10.9714V5.9368C13.0885 4.27829 12.0817 3.37598 10.4566 3.37598Z" stroke="#151515" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  <Path d="M5.01562 7.625H5.02229" stroke="#151515" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  <Path d="M5.01562 9.92773H5.02229" stroke="#151515" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  <Path d="M7.69141 7.625H7.69807" stroke="#151515" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  <Path d="M7.69141 9.92773H7.69807" stroke="#151515" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  <Path d="M10.3594 7.625H10.366" stroke="#151515" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  <Path d="M10.3594 9.92773H10.366" stroke="#151515" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </Svg>
                <Text style={styles.statusText}>{displayText}</Text>
              </View>
            </View>
          </LinearGradient>
        </ImageBackground>
      </View>

      {/* ── Footer ── */}
      <View style={styles.footer}>
        {/* Overlapping avatar stack */}
        <View style={styles.avatars}>
          {[0, 1, 2].map((i) => (
            <View key={i} style={{ marginLeft: i > 0 ? -8 : 0 }}>
              <Avatar size={32} outerBorderWidth={4} borderColor="#FFFFFF" />
            </View>
          ))}
        </View>

        {/* Actions */}
        <View style={styles.actions}>
          <TouchableOpacity
            style={styles.arrowButton}
            onPress={(e) => {
              e.stopPropagation();
              onPress();
            }}
            activeOpacity={0.7}
          >
            <Svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <Path d="M6 12L10 8L6 4" stroke="#151515" strokeWidth="2" strokeLinecap="square" />
            </Svg>
          </TouchableOpacity>
        </View>
      </View>
    </TouchableOpacity>
  );
}

const styles = StyleSheet.create({
  card: {
    backgroundColor: '#FFFFFF',
    borderRadius: 16,
    borderWidth: 1,
    borderColor: '#F0EEE4',
    overflow: 'hidden',
  },
  header: {
    height: 150,
  },
  image: {
    width: '100%',
    height: '100%',
  },
  imageStyle: {
    resizeMode: 'cover',
  },
  gradient: {
    flex: 1,
    justifyContent: 'flex-end',
    padding: 24,
  },
  content: {
    gap: 12,
  },
  title: {
    fontSize: 20,
    fontFamily: 'Sentient-Regular',
    color: '#151515',
    lineHeight: 28,
  },
  statusRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  statusText: {
    fontSize: 12,
    fontFamily: 'Inter_700Bold',
    color: '#151515',
    lineHeight: 12,
  },
  footer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 24,
    paddingBottom: 24,
    paddingTop: 0,
  },
  avatars: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  actions: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  arrowButton: {
    backgroundColor: '#F9F8F3',
    borderRadius: 12,
    width: 32,
    height: 32,
    justifyContent: 'center',
    alignItems: 'center',
  },
});
